import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { FiRefreshCw, FiCheck, FiExternalLink, FiBriefcase, FiUser, FiKey, FiX, FiCheckCircle, FiAlertCircle, FiEye, FiEyeOff, FiCopy } from "react-icons/fi";
import ProfileSidebar from "./sidebar/ProfileSidebar";
import ConfirmationPopup from "./common/ConfirmationPopup";
import "../assets/css/integrations.css";
import "../assets/css/settings-pages.css";

// Google services that support work/personal accounts
const GOOGLE_SERVICES = ["gmail", "google_calendar", "google_drive", "google_docs", "google_sheets"];

// Microsoft services that support work/personal accounts
const MICROSOFT_SERVICES = ["outlook"];

// Official brand colors and SVG icons for each integration
const INTEGRATIONS = [
  {
    id: "gmail",
    name: "Gmail",
    description: "Send, read, and manage emails",
    color: "#EA4335",
    isGoogleService: true,
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#4285F4" d="M22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4H20C21.1 4 22 4.9 22 6Z" opacity="0.3"/>
        <path fill="#EA4335" d="M22 6L12 13L2 6V4L12 11L22 4V6Z"/>
        <path fill="#FBBC05" d="M2 6L12 13V11L2 4V6Z"/>
        <path fill="#34A853" d="M22 6L12 13V11L22 4V6Z"/>
      </svg>
    ),
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Schedule meetings and manage events",
    color: "#4285F4",
    isGoogleService: true,
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#4285F4" d="M19 4H18V2H16V4H8V2H6V4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4Z"/>
        <path fill="#fff" d="M19 20H5V9H19V20Z"/>
        <path fill="#EA4335" d="M12 14H17V12H12V14Z"/>
        <path fill="#FBBC05" d="M7 14H10V12H7V14Z"/>
        <path fill="#34A853" d="M7 17H10V15H7V17Z"/>
        <path fill="#4285F4" d="M12 17H17V15H12V17Z"/>
      </svg>
    ),
  },
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Access and manage your files",
    color: "#0F9D58",
    isGoogleService: true,
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#4285F4" d="M12 2L4 14H8L16 2H12Z"/>
        <path fill="#0F9D58" d="M16 2L8 14L12 22L20 10L16 2Z"/>
        <path fill="#FBBC05" d="M4 14L8 22H20L16 14H4Z"/>
      </svg>
    ),
  },
  {
    id: "google_docs",
    name: "Google Docs",
    description: "Create and edit documents",
    color: "#4285F4",
    isGoogleService: true,
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#4285F4" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"/>
        <path fill="#fff" d="M14 2V8H20L14 2Z" opacity="0.5"/>
        <path fill="#fff" d="M16 13H8V11H16V13ZM14 17H8V15H14V17Z"/>
      </svg>
    ),
  },
  {
    id: "google_sheets",
    name: "Google Sheets",
    description: "Work with spreadsheets",
    color: "#0F9D58",
    isGoogleService: true,
    disabled: true, // Disabled - not currently available
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#0F9D58" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"/>
        <path fill="#fff" d="M14 2V8H20L14 2Z" opacity="0.5"/>
        <path fill="#fff" d="M16 17H8V11H16V17ZM10 13V15H14V13H10Z"/>
      </svg>
    ),
  },
  {
    id: "notion",
    name: "Notion",
    description: "Search and manage your workspace",
    color: "#000000",
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="currentColor" d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 2.163c-.42-.326-.98-.7-2.055-.607L3.01 2.71c-.466.046-.56.28-.374.466l1.823 1.032zm.793 3.172v13.585c0 .746.373 1.026 1.213.98l14.523-.84c.84-.046.933-.56.933-1.166V6.35c0-.606-.233-.932-.746-.886l-15.177.886c-.56.047-.746.327-.746.933zm14.337.699c.093.42 0 .84-.42.886l-.7.14v10.04c-.607.327-1.166.514-1.633.514-.746 0-.933-.234-1.493-.933l-4.571-7.192v6.958l1.446.327s0 .84-1.166.84l-3.218.186c-.093-.187 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.451-.233 4.758 7.284V9.2l-1.213-.14c-.093-.513.28-.886.746-.933l3.232-.047zm-15.643-2.8L17.89 4.38c1.4-.093 1.773.093 2.66.746l2.193 1.54c.56.373.746.7.746 1.306v14.025c0 1.026-.373 1.633-1.679 1.726l-15.457.933c-.98.047-1.446-.093-1.959-.746l-2.473-3.218c-.56-.746-.793-1.306-.793-1.96V6.086c0-.84.373-1.54 1.306-1.633l.026-.173z"/>
      </svg>
    ),
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Manage CRM and marketing",
    color: "#FF7A59",
    disabled: true, // Disabled - not currently available
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path fill="#FF7A59" d="M18.164 7.93V5.084a2.198 2.198 0 0 0 1.267-1.984 2.21 2.21 0 0 0-2.212-2.212 2.21 2.21 0 0 0-2.212 2.212c0 .878.517 1.632 1.258 1.987v2.842a5.575 5.575 0 0 0-2.465 1.17l-6.517-5.07a2.437 2.437 0 0 0 .077-.597A2.42 2.42 0 0 0 4.942.888a2.421 2.421 0 0 0 0 4.842c.396 0 .77-.098 1.1-.269l6.442 5.012a5.582 5.582 0 0 0-.546 2.41c0 .869.198 1.691.55 2.429l-2.045 2.044a2.12 2.12 0 0 0-.69-.12 2.159 2.159 0 1 0 2.159 2.159c0-.247-.044-.483-.12-.703l2.01-2.01a5.58 5.58 0 1 0 4.362-8.752z"/>
      </svg>
    ),
  },
  {
    id: "outlook",
    name: "Microsoft Outlook",
    description: "Calendar, email, and contacts — one login for all",
    color: "#0078D4",
    isMicrosoftService: true,
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <rect x="1" y="3" width="22" height="18" rx="3" fill="#0078D4" opacity="0.12"/>
        <path fill="#0078D4" d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"/>
        <rect x="14" y="14" width="5" height="4" rx="0.5" fill="#0078D4" opacity="0.5"/>
        <text x="16.5" y="17.2" fontSize="3.5" fill="white" textAnchor="middle" fontWeight="bold">31</text>
      </svg>
    ),
  },
  {
    id: "fireflies",
    name: "Fireflies.ai",
    description: "Meeting transcription and notes",
    color: "#7C3AED",
    isApiKeyBased: true,
    apiKeyLink: "https://app.fireflies.ai/integrations/custom/fireflies",
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <circle cx="12" cy="12" r="10" fill="#7C3AED" opacity="0.2"/>
        <path fill="#7C3AED" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        <circle cx="8" cy="8" r="2" fill="#A855F7"/>
        <circle cx="16" cy="8" r="2" fill="#A855F7"/>
        <circle cx="12" cy="16" r="2" fill="#A855F7"/>
      </svg>
    ),
  },
  {
    id: "twilio",
    name: "Twilio SMS",
    description: "Send and receive SMS messages",
    color: "#F22F46",
    isTwilio: true,
    apiKeyLink: "https://console.twilio.com",
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <circle cx="12" cy="12" r="10" fill="#F22F46"/>
        <circle cx="8.5" cy="9.5" r="2" fill="white"/>
        <circle cx="15.5" cy="9.5" r="2" fill="white"/>
        <circle cx="8.5" cy="14.5" r="2" fill="white"/>
        <circle cx="15.5" cy="14.5" r="2" fill="white"/>
      </svg>
    ),
  },
  {
    id: "quo",
    name: "Quo SMS",
    description: "Send and receive SMS via Quo (OpenPhone)",
    color: "#5B5FC7",
    isApiKeyBased: true,
    apiKeyLink: "https://app.openphone.com/settings",
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <rect x="2" y="2" width="20" height="20" rx="6" fill="#5B5FC7" opacity="0.15"/>
        <path fill="#5B5FC7" d="M16.5 14.5c-.7 0-1.4-.1-2-.4l-1.4.8c-.7-.5-1.3-1.1-1.8-1.8l.8-1.4c-.3-.6-.4-1.3-.4-2 0-1.1.3-2.1.8-2.9C13.2 5.6 14.5 5 16 5c2.8 0 5 2.2 5 5s-2.2 5-4.5 4.5zM8 19c-1.7 0-3-1.3-3-3V8c0-1.7 1.3-3 3-3h1v2H8c-.6 0-1 .4-1 1v8c0 .6.4 1 1 1h8c.6 0 1-.4 1-1v-1h2v1c0 1.7-1.3 3-3 3H8z"/>
      </svg>
    ),
  },
];

function Integrations({ toggleTheme, isDarkMode, embedded = false, userId: propUserId }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState({});
  const [showApiKeyInput, setShowApiKeyInput] = useState({});
  const [apiKeyStatus, setApiKeyStatus] = useState({});
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Twilio-specific state
  const [twilioInput, setTwilioInput] = useState({ accountSid: "", authToken: "", phoneNumber: "" });
  const [showTwilioInput, setShowTwilioInput] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);

  // Fireflies webhook setup state
  const [showFirefliesWebhook, setShowFirefliesWebhook] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);

  // Handle sidebar menu changes
  const handleMenuChange = (menuId) => {
    // Handle logout
    if (menuId === 8) {
      setShowLogoutModal(true);
      return;
    }
    // Handle theme toggle (id 7) - do nothing, toggle handles itself
    if (menuId === 7) {
      return;
    }
    // Navigate to profile page for other menu items
    if (menuId !== 2) {
      navigate("/userprofile", { state: { activeMenu: menuId } });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setShowLogoutModal(false);
    navigate("/login");
  };
  const API_URL = process.env.REACT_APP_ZOOMEY_API_URL;
  // Next.js API URL for Composio-based integrations
  const COMPOSIO_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";

  // Show toast notification
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Handle OAuth callback - check URL params for success/error
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");
    const app = urlParams.get("app");
    const errorMsg = urlParams.get("error");

    if (status === "connected" && app) {
      showToast("success", `${app.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())} connected successfully!`);
      // Clean up URL without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === "error" || errorMsg) {
      showToast("error", errorMsg || "Connection failed. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    async function init() {
      try {
        // If embedded and userId is provided, use it directly
        if (embedded && propUserId) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            setUser(session.user);
          }
          await fetchConnections(propUserId);
          setLoading(false);
          return;
        }

        // Otherwise, get user from session
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await fetchConnections(session.user.id);
        }
      } catch (err) {
        console.error("Error initializing integrations:", err);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [embedded, propUserId]);

  const fetchConnections = async (userId) => {
    try {
      // Use Composio/Next.js API for status
      const connRes = await fetch(`${COMPOSIO_API_URL}/api/integrations/status?user_id=${userId}`);
      const connData = await connRes.json();
      if (connData.success) {
        setConnections(connData.connections || []);
      }
    } catch (err) {
      console.error("Error fetching integrations:", err);
    }
  };

  const handleConnect = async (appId, accountType = null) => {
    if (!user) return;

    // For Google services, include account type
    const connectId = accountType ? `${appId}_${accountType}` : appId;
    setConnecting(connectId);
    setError(null);

    try {
      // Build callback URL to return to integrations page with status
      const callbackUrl = `${window.location.origin}/integrations?status=connected&app=${appId}`;

      // Use Composio/Next.js API for Google services
      const res = await fetch(`${COMPOSIO_API_URL}/api/integrations/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          app_name: appId,
          account_type: accountType, // 'work' or 'personal'
          callback_url: callbackUrl
        })
      });

      const data = await res.json();

      if (data.success && data.redirect_url) {
        // Store account type in sessionStorage for callback handling
        if (accountType) {
          sessionStorage.setItem('pending_integration', JSON.stringify({
            app_name: appId,
            account_type: accountType
          }));
        }
        window.location.href = data.redirect_url;
      } else {
        showToast("error", data.error || "Failed to initiate connection");
        setConnecting(null);
      }
    } catch (err) {
      console.error("Connection error:", err);
      showToast("error", "Failed to connect. Please try again.");
      setConnecting(null);
    }
  };

  const handleDisconnect = async (appId, accountType = null) => {
    if (!user) return;

    const connectId = accountType ? `${appId}_${accountType}` : appId;
    setConnecting(connectId);

    try {
      // Use Composio/Next.js API for disconnect
      const res = await fetch(`${COMPOSIO_API_URL}/api/integrations/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          app_name: appId,
          account_type: accountType
        })
      });

      const data = await res.json();

      if (data.success) {
        await fetchConnections(user.id);
        const appName = appId.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase());
        showToast("success", `${appName} disconnected successfully`);
      } else {
        showToast("error", data.error || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Disconnect error:", err);
      showToast("error", "Failed to disconnect. Please try again.");
    } finally {
      setConnecting(null);
    }
  };

  // API URL for Next.js backend (for API key operations) - same as COMPOSIO_API_URL
  const NEXT_API_URL = COMPOSIO_API_URL;

  // Handle API key save for services like Fireflies
  const handleSaveApiKey = async (appId) => {
    if (!user) return;

    const apiKey = apiKeyInput[appId]?.trim();
    if (!apiKey) {
      setError("Please enter an API key");
      return;
    }

    setConnecting(appId);
    setError(null);

    try {
      // Use Next.js API for API key operations
      const apiUrl = NEXT_API_URL
        ? `${NEXT_API_URL}/api/integrations/save-api-key`
        : "/api/integrations/save-api-key";

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          app_name: appId,
          api_key: apiKey
        })
      });

      const data = await res.json();

      if (data.success) {
        await fetchConnections(user.id);
        setShowApiKeyInput(prev => ({ ...prev, [appId]: false }));
        setApiKeyInput(prev => ({ ...prev, [appId]: "" }));
        setApiKeyStatus(prev => ({ ...prev, [appId]: "connected" }));
        const appName = INTEGRATIONS.find(a => a.id === appId)?.name || appId;
        showToast("success", `${appName} API key saved successfully!`);
      } else {
        showToast("error", data.error || "Failed to save API key");
      }
    } catch (err) {
      console.error("API key save error:", err);
      showToast("error", "Failed to save API key. Please try again.");
    } finally {
      setConnecting(null);
    }
  };

  // Handle API key removal
  const handleRemoveApiKey = async (appId) => {
    if (!user) return;

    setConnecting(appId);
    setError(null);

    try {
      // Use Next.js API for API key operations
      const apiUrl = NEXT_API_URL
        ? `${NEXT_API_URL}/api/integrations/remove-api-key`
        : "/api/integrations/remove-api-key";

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          app_name: appId
        })
      });

      const data = await res.json();

      if (data.success) {
        await fetchConnections(user.id);
        setApiKeyStatus(prev => ({ ...prev, [appId]: null }));
        const appName = INTEGRATIONS.find(a => a.id === appId)?.name || appId;
        showToast("success", `${appName} API key removed`);
      } else {
        showToast("error", data.error || "Failed to remove API key");
      }
    } catch (err) {
      console.error("API key remove error:", err);
      showToast("error", "Failed to remove API key. Please try again.");
    } finally {
      setConnecting(null);
    }
  };

  // Handle Twilio credentials save
  const handleSaveTwilio = async () => {
    if (!user) return;
    const { accountSid, authToken, phoneNumber } = twilioInput;
    if (!accountSid || !authToken || !phoneNumber) {
      showToast("error", "Please fill in all Twilio fields");
      return;
    }
    setConnecting("twilio");
    try {
      const apiUrl = NEXT_API_URL
        ? `${NEXT_API_URL}/api/integrations/save-api-key`
        : "/api/integrations/save-api-key";
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          app_name: "Twilio",
          api_key: JSON.stringify({ account_sid: accountSid, auth_token: authToken, phone_number: phoneNumber })
        })
      });
      const data = await res.json();
      if (data.success) {
        await fetchConnections(user.id);
        setShowTwilioInput(false);
        setTwilioInput({ accountSid: "", authToken: "", phoneNumber: "" });
        showToast("success", "Twilio SMS connected successfully!");
      } else {
        showToast("error", data.error || "Failed to save Twilio credentials");
      }
    } catch (err) {
      console.error("Twilio save error:", err);
      showToast("error", "Failed to save Twilio credentials");
    } finally {
      setConnecting(null);
    }
  };

  // Check connection for specific app and optional account type
  const getConnectionByType = useCallback((appId, accountType = null) => {
    return connections.find(conn => {
      // Normalize both app names for comparison
      const connAppNormalized = conn.app?.toLowerCase();
      const appIdNormalized = appId.toLowerCase();

      // Direct match (e.g., "gmail" === "gmail", "google_calendar" === "google_calendar")
      const appMatches = connAppNormalized === appIdNormalized;

      if (!appMatches) return false;

      // If no account type specified, return first match
      if (!accountType) return true;

      // Check if connection has matching account type
      return conn.account_type === accountType;
    });
  }, [connections]);

  const isConnectedByType = useCallback((appId, accountType = null) => {
    return !!getConnectionByType(appId, accountType);
  }, [getConnectionByType]);

  // Render Google service card with work/personal options
  const renderGoogleServiceCard = (app, index) => {
    const workConnection = getConnectionByType(app.id, 'work');
    const personalConnection = getConnectionByType(app.id, 'personal');
    const hasAnyConnection = workConnection || personalConnection;
    const isWorkLoading = connecting === `${app.id}_work`;
    const isPersonalLoading = connecting === `${app.id}_personal`;

    return (
      <div
        key={app.id}
        className={`integration-card google-service ${hasAnyConnection ? "connected" : ""}`}
        style={{
          "--accent-color": app.color,
          "--animation-delay": `${index * 0.05}s`
        }}
      >
        {/* Active Badge - Top Right Corner */}
        {hasAnyConnection && (
          <div className="active-badge">
            <span className="active-badge-dot"></span>
            Active
          </div>
        )}

        <div className="card-icon" style={{ backgroundColor: `${app.color}15` }}>
          {app.icon}
        </div>

        <div className="card-info">
          <h3>{app.name}</h3>
          <p>{app.description}</p>
        </div>

        {/* Work Account Row */}
        <div className="account-connection-row">
          <div className="account-label">
            <FiBriefcase className="account-label-icon" />
            <div>
              <span>Work</span>
              {workConnection?.account_email && (
                <div className="account-email-small">{workConnection.account_email}</div>
              )}
            </div>
          </div>
          <div className="account-status">
            {workConnection ? (
              <>
                <span className="status-dot connected"></span>
                <button
                  className="btn-disconnect-small"
                  onClick={() => handleDisconnect(app.id, 'work')}
                  disabled={isWorkLoading}
                >
                  {isWorkLoading ? <FiRefreshCw className="btn-spinner" size={12} /> : "Disconnect"}
                </button>
              </>
            ) : (
              <button
                className="btn-connect-small"
                onClick={() => handleConnect(app.id, 'work')}
                disabled={isWorkLoading}
                style={{ "--btn-color": app.color }}
              >
                {isWorkLoading ? (
                  <FiRefreshCw className="btn-spinner" size={12} />
                ) : (
                  <>
                    <FiExternalLink size={12} />
                    Connect
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Personal Account Row */}
        <div className="account-connection-row">
          <div className="account-label">
            <FiUser className="account-label-icon" />
            <div>
              <span>Personal</span>
              {personalConnection?.account_email && (
                <div className="account-email-small">{personalConnection.account_email}</div>
              )}
            </div>
          </div>
          <div className="account-status">
            {personalConnection ? (
              <>
                <span className="status-dot connected"></span>
                <button
                  className="btn-disconnect-small"
                  onClick={() => handleDisconnect(app.id, 'personal')}
                  disabled={isPersonalLoading}
                >
                  {isPersonalLoading ? <FiRefreshCw className="btn-spinner" size={12} /> : "Disconnect"}
                </button>
              </>
            ) : (
              <button
                className="btn-connect-small"
                onClick={() => handleConnect(app.id, 'personal')}
                disabled={isPersonalLoading}
                style={{ "--btn-color": app.color }}
              >
                {isPersonalLoading ? (
                  <FiRefreshCw className="btn-spinner" size={12} />
                ) : (
                  <>
                    <FiExternalLink size={12} />
                    Connect
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render regular (non-Google) service card
  const renderRegularCard = (app, index) => {
    const connected = isConnectedByType(app.id);
    const connection = getConnectionByType(app.id);
    const isLoading = connecting === app.id;

    return (
      <div
        key={app.id}
        className={`integration-card ${connected ? "connected" : ""}`}
        style={{ 
          "--accent-color": app.color,
          "--animation-delay": `${index * 0.05}s`
        }}
      >
        <div className="card-icon" style={{ backgroundColor: `${app.color}15` }}>
          {app.icon}
        </div>
        
        <div className="card-info">
          <h3>{app.name}</h3>
          <p>{app.description}</p>
          {connected && connection?.account_email && (
            <span className="connected-email">{connection.account_email}</span>
          )}
        </div>

        <div className="card-actions">
          {connected ? (
            <>
              <span className="status-badge connected">
                <FiCheck size={14} />
                Connected
              </span>
              <button
                className="btn-disconnect"
                onClick={() => handleDisconnect(app.id)}
                disabled={isLoading}
              >
                {isLoading ? <FiRefreshCw className="btn-spinner" /> : "Disconnect"}
              </button>
            </>
          ) : (
            <button
              className="btn-connect"
              onClick={() => handleConnect(app.id)}
              disabled={isLoading}
              style={{ "--btn-color": app.color }}
            >
              {isLoading ? (
                <FiRefreshCw className="btn-spinner" />
              ) : (
                <>
                  <FiExternalLink size={16} />
                  Connect
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render API key-based service card (like Fireflies)
  const renderApiKeyCard = (app, index) => {
    const connected = isConnectedByType(app.id);
    const isLoading = connecting === app.id;
    const isShowingInput = showApiKeyInput[app.id];
    const isFireflies = app.id === 'fireflies';

    return (
      <div
        key={app.id}
        className={`integration-card api-key-card ${connected ? "connected" : ""}`}
        style={{
          "--accent-color": app.color,
          "--animation-delay": `${index * 0.05}s`
        }}
      >
        <div className="card-icon" style={{ backgroundColor: `${app.color}15` }}>
          {app.icon}
        </div>

        <div className="card-info">
          <h3>{app.name}</h3>
          <p>{app.description}</p>
          {connected && (
            <span className="connected-status">
              <FiCheck size={12} /> API Key configured
            </span>
          )}
        </div>

        <div className="card-actions api-key-actions">
          {connected ? (
            <>
              <span className="status-badge connected">
                <FiCheck size={14} />
                Connected
              </span>
              {/* Fireflies: Show Setup Meetings button */}
              {isFireflies && (
                <button
                  className="btn-connect-meeting"
                  onClick={() => setShowFirefliesWebhook(true)}
                  style={{
                    background: "linear-gradient(135deg, #7C3AED, #9333EA)",
                    color: "white",
                    border: "none",
                    padding: "10px 18px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(124, 58, 237, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(124, 58, 237, 0.3)";
                  }}
                >
                  <FiExternalLink size={16} />
                  Connect with Meeting
                </button>
              )}
              <button
                className="btn-disconnect"
                onClick={() => handleRemoveApiKey(app.id)}
                disabled={isLoading}
              >
                {isLoading ? <FiRefreshCw className="btn-spinner" /> : "Remove"}
              </button>
            </>
          ) : isShowingInput ? (
            <div className="api-key-input-container">
              <input
                type="password"
                placeholder="Paste your API key"
                value={apiKeyInput[app.id] || ""}
                onChange={(e) => setApiKeyInput(prev => ({ ...prev, [app.id]: e.target.value }))}
                className="api-key-input"
                autoFocus
              />
              <div className="api-key-buttons">
                <button
                  className="btn-save-key"
                  onClick={() => handleSaveApiKey(app.id)}
                  disabled={isLoading || !apiKeyInput[app.id]?.trim()}
                  style={{ "--btn-color": app.color }}
                >
                  {isLoading ? <FiRefreshCw className="btn-spinner" size={14} /> : "Save"}
                </button>
                <button
                  className="btn-cancel-key"
                  onClick={() => {
                    setShowApiKeyInput(prev => ({ ...prev, [app.id]: false }));
                    setApiKeyInput(prev => ({ ...prev, [app.id]: "" }));
                  }}
                >
                  Cancel
                </button>
              </div>
              {app.apiKeyLink && (
                <a
                  href={app.apiKeyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="get-api-key-link"
                >
                  <FiExternalLink size={12} /> Get your API key
                </a>
              )}
            </div>
          ) : (
            <button
              className="btn-connect"
              onClick={() => setShowApiKeyInput(prev => ({ ...prev, [app.id]: true }))}
              disabled={isLoading}
              style={{ "--btn-color": app.color }}
            >
              <FiKey size={16} />
              Add API Key
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render Twilio card with 3 input fields
  const renderTwilioCard = (app, index) => {
    const connected = isConnectedByType(app.id);
    const isLoading = connecting === app.id;
    return (
      <div
        key={app.id}
        className={`integration-card api-key-card ${connected ? "connected" : ""}`}
        style={{ "--accent-color": app.color, "--animation-delay": `${index * 0.05}s` }}
      >
        <div className="card-icon" style={{ backgroundColor: `${app.color}15` }}>{app.icon}</div>
        <div className="card-info">
          <h3>{app.name}</h3>
          <p>{app.description}</p>
          {connected && <span className="connected-status"><FiCheck size={12} /> Configured</span>}
        </div>
        <div className="card-actions api-key-actions">
          {connected ? (
            <>
              <span className="status-badge connected"><FiCheck size={14} />Connected</span>
              <button className="btn-disconnect" onClick={() => handleRemoveApiKey(app.id)} disabled={isLoading}>
                {isLoading ? <FiRefreshCw className="btn-spinner" /> : "Remove"}
              </button>
            </>
          ) : showTwilioInput ? (
            <div className="api-key-input-container" style={{ gap: "8px" }}>
              <input type="text" placeholder="Account SID (AC...)" value={twilioInput.accountSid}
                onChange={(e) => setTwilioInput(prev => ({ ...prev, accountSid: e.target.value }))}
                className="api-key-input" style={{ marginBottom: "6px" }} />
              <div style={{ position: "relative" }}>
                <input type={showTwilioToken ? "text" : "password"} placeholder="Auth Token"
                  value={twilioInput.authToken}
                  onChange={(e) => setTwilioInput(prev => ({ ...prev, authToken: e.target.value }))}
                  className="api-key-input" style={{ paddingRight: "36px" }} />
                <button type="button" onClick={() => setShowTwilioToken(!showTwilioToken)}
                  style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: "var(--text)", opacity: 0.6 }}>
                  {showTwilioToken ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              <input type="text" placeholder="Phone Number (+1234567890)" value={twilioInput.phoneNumber}
                onChange={(e) => setTwilioInput(prev => ({ ...prev, phoneNumber: e.target.value }))}
                className="api-key-input" style={{ marginTop: "6px" }} />
              <div className="api-key-buttons">
                <button className="btn-save-key" onClick={handleSaveTwilio}
                  disabled={isLoading || !twilioInput.accountSid || !twilioInput.authToken || !twilioInput.phoneNumber}
                  style={{ "--btn-color": app.color }}>
                  {isLoading ? <FiRefreshCw className="btn-spinner" size={14} /> : "Save"}
                </button>
                <button className="btn-cancel-key" onClick={() => {
                  setShowTwilioInput(false);
                  setTwilioInput({ accountSid: "", authToken: "", phoneNumber: "" });
                }}>Cancel</button>
              </div>
              <a href={app.apiKeyLink} target="_blank" rel="noopener noreferrer" className="get-api-key-link">
                <FiExternalLink size={12} /> Get credentials from Twilio Console
              </a>
            </div>
          ) : (
            <button className="btn-connect" onClick={() => setShowTwilioInput(true)} disabled={isLoading}
              style={{ "--btn-color": app.color }}>
              <FiKey size={16} /> Configure
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render the integrations content (shared between embedded and standalone)
  const renderIntegrationsContent = () => (
    <main className="integrations-content">
      {error && (
        <div className="integrations-error" onClick={() => setError(null)}>
          <span>{error}</span>
          <button>&times;</button>
        </div>
      )}

      <div className="integrations-grid">
        {INTEGRATIONS.filter(app => !app.disabled).map((app, index) => {
          if (app.isGoogleService || app.isMicrosoftService) {
            return renderGoogleServiceCard(app, index);
          } else if (app.isTwilio) {
            return renderTwilioCard(app, index);
          } else if (app.isApiKeyBased) {
            return renderApiKeyCard(app, index);
          } else {
            return renderRegularCard(app, index);
          }
        })}
      </div>

      {/* Info Section */}
      <div className="integrations-info">
        <h2>Why connect your apps?</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-icon">📧</span>
            <div>
              <strong>Email</strong>
              <p>Send, read, and manage emails through natural conversation</p>
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">📅</span>
            <div>
              <strong>Calendar</strong>
              <p>Schedule meetings, check availability, get reminders</p>
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">📁</span>
            <div>
              <strong>Files</strong>
              <p>Search, organize, and access your documents</p>
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">💬</span>
            <div>
              <strong>Communication</strong>
              <p>Send messages and stay connected across platforms</p>
            </div>
          </div>
        </div>
        <div className="info-grid" style={{ marginTop: '16px' }}>
          <div className="info-item">
            <span className="info-icon">💼</span>
            <div>
              <strong>Work & Personal</strong>
              <p>Connect both your work and personal Google accounts separately</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );

  // Loading state
  if (loading) {
    if (embedded) {
      return (
        <div className="settings-page-container">
          <div className="settings-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="integrations-loading">
        <FiRefreshCw className="integrations-spinner" />
        <span>Loading integrations...</span>
      </div>
    );
  }

  // Fireflies Webhook Modal Component (reusable)
  const renderFirefliesWebhookModal = () => {
    if (!showFirefliesWebhook) return null;

    const webhookUrl = (process.env.REACT_APP_FIREFLIES_WEBHOOK_URL || "http://localhost:3000/api/fireflies/webhook/[userId]").replace("[userId]", user?.id || "");

    return (
      <div
        className="fireflies-modal-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px"
        }}
        onClick={() => setShowFirefliesWebhook(false)}
      >
        <div
          className="fireflies-modal"
          style={{
            background: "var(--background)",
            borderRadius: "20px",
            width: "100%",
            maxWidth: "520px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            animation: "modalSlideIn 0.3s ease"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{
            background: "linear-gradient(135deg, #7C3AED, #9333EA)",
            padding: "24px 28px",
            color: "white"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "28px" }}>🎯</span>
                  <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700 }}>Connect with Meeting</h2>
                </div>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "14px" }}>
                  Get your meeting transcripts automatically
                </p>
              </div>
              <button
                onClick={() => setShowFirefliesWebhook(false)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px",
                  cursor: "pointer",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div style={{ padding: "28px" }}>
            {/* Webhook URL Section */}
            <div style={{ marginBottom: "24px" }}>
              <label style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--text)",
                display: "block",
                marginBottom: "10px"
              }}>
                📋 Your Webhook URL
              </label>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: "var(--chatbot-bg)",
                border: "2px solid var(--border-color)",
                borderRadius: "12px",
                padding: "14px 16px"
              }}>
                <code style={{
                  flex: 1,
                  fontSize: "13px",
                  color: "#7C3AED",
                  wordBreak: "break-all",
                  fontFamily: "monospace"
                }}>
                  {webhookUrl}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(webhookUrl);
                    setWebhookCopied(true);
                    setTimeout(() => setWebhookCopied(false), 2000);
                  }}
                  style={{
                    background: webhookCopied ? "#10B981" : "#7C3AED",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                    minWidth: "90px",
                    justifyContent: "center"
                  }}
                >
                  {webhookCopied ? <FiCheck size={16} /> : <FiCopy size={16} />}
                  {webhookCopied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            {/* Steps Section */}
            <div style={{ marginBottom: "24px" }}>
              <label style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--text)",
                display: "block",
                marginBottom: "12px"
              }}>
                📝 Follow these steps
              </label>
              <div style={{
                background: "var(--chatbot-bg)",
                borderRadius: "12px",
                padding: "16px 20px"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "#7C3AED",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      flexShrink: 0
                    }}>1</span>
                    <span style={{ fontSize: "14px", color: "var(--text)" }}>
                      Go to{" "}
                      <a
                        href="https://app.fireflies.ai/settings"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#7C3AED", fontWeight: 600, textDecoration: "none" }}
                      >
                        Fireflies Settings <FiExternalLink size={12} style={{ marginLeft: "2px" }} />
                      </a>
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "#7C3AED",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      flexShrink: 0
                    }}>2</span>
                    <span style={{ fontSize: "14px", color: "var(--text)" }}>
                      Click on <strong>"Developer"</strong> tab
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "#7C3AED",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      flexShrink: 0
                    }}>3</span>
                    <span style={{ fontSize: "14px", color: "var(--text)" }}>
                      Paste the webhook URL in <strong>"Webhooks"</strong> field
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "#10B981",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      flexShrink: 0
                    }}>✓</span>
                    <span style={{ fontSize: "14px", color: "var(--text)" }}>
                      Click <strong>"Save"</strong> and you're done!
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Docs Tip */}
            <div style={{
              background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
              borderRadius: "12px",
              padding: "16px",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px"
            }}>
              <span style={{ fontSize: "24px" }}>💡</span>
              <div>
                <p style={{
                  margin: "0 0 4px 0",
                  fontWeight: 600,
                  color: "#4338CA",
                  fontSize: "14px"
                }}>
                  For better experience
                </p>
                <p style={{
                  margin: 0,
                  color: "#5145CD",
                  fontSize: "13px"
                }}>
                  Connect with Google Docs to automatically save meeting notes as documents!
                </p>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div style={{
            padding: "20px 28px",
            borderTop: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px"
          }}>
            <button
              onClick={() => setShowFirefliesWebhook(false)}
              style={{
                padding: "12px 24px",
                borderRadius: "10px",
                border: "1px solid var(--border-color)",
                background: "transparent",
                color: "var(--text)",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer"
              }}
            >
              Close
            </button>
            <button
              onClick={() => {
                window.open("https://app.fireflies.ai/settings", "_blank");
              }}
              style={{
                padding: "12px 24px",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, #7C3AED, #9333EA)",
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              Open Fireflies Settings
              <FiExternalLink size={16} />
            </button>
          </div>
        </div>

        <style>{`
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(-10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}</style>
      </div>
    );
  };

  // Embedded mode - render just the content
  if (embedded) {
    return (
      <div className="settings-page-container integrations-embedded">
        {/* Toast Notification */}
        {toast && (
          <div className={`integration-toast ${toast.type}`}>
            {toast.type === "success" ? (
              <FiCheckCircle size={20} />
            ) : (
              <FiAlertCircle size={20} />
            )}
            <span>{toast.message}</span>
            <button className="toast-close" onClick={() => setToast(null)}>
              <FiX size={16} />
            </button>
          </div>
        )}
        {renderIntegrationsContent()}

        {/* Fireflies Webhook Modal */}
        {renderFirefliesWebhookModal()}
      </div>
    );
  }

  // Standalone mode - render full page with sidebar
  return (
    <>
      <div
        className="d-flex"
        style={{ minHeight: "100vh", background: "var(--background)" }}
        data-theme={isDarkMode ? "dark" : "light"}
      >
        {/* Sidebar */}
        <div
          style={{
            position: "fixed",
            height: "100vh",
            zIndex: 10,
          }}
        >
          <ProfileSidebar
            onMenuChange={handleMenuChange}
            activeMenu={2}
            toggleTheme={toggleTheme}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Main Content Area */}
        <div
          className="flex-grow-1"
          style={{
            background: "var(--chatbot-bg)",
            marginLeft: "260px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Top Header Bar */}
          <div
            style={{
              background: "var(--background)",
              borderBottom: "1px solid var(--border-color)",
              padding: "16px 32px",
              flexShrink: 0,
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                {/* Logo */}
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, var(--orange-bg), #ff7b00)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(250, 84, 0, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => navigate("/chat-bot")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(250, 84, 0, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(250, 84, 0, 0.3)";
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "18px",
                      userSelect: "none",
                    }}
                  >
                    Z
                  </span>
                </div>
                {/* Title */}
                <div>
                  <h5
                    style={{
                      margin: 0,
                      color: "var(--text)",
                      fontWeight: 700,
                      fontSize: "20px",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Integrations
                  </h5>
                  <p
                    style={{
                      margin: 0,
                      color: "var(--text)",
                      opacity: 0.6,
                      fontSize: "13px",
                    }}
                  >
                    Connect your favorite apps to Zoomey
                  </p>
                </div>
              </div>

              {/* Right side - Back to Chat button */}
              <button
                onClick={() => navigate("/chat-bot")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "1px solid var(--border-color)",
                  background: "var(--background)",
                  color: "var(--text)",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--teal-bg)";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "var(--teal-bg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--background)";
                  e.currentTarget.style.color = "var(--text)";
                  e.currentTarget.style.borderColor = "var(--border-color)";
                }}
              >
                <i className="bi bi-chat-dots-fill"></i>
                Back to Chat
              </button>
            </div>
          </div>

          {/* Toast Notification */}
          {toast && (
            <div className={`integration-toast ${toast.type}`}>
              {toast.type === "success" ? (
                <FiCheckCircle size={20} />
              ) : (
                <FiAlertCircle size={20} />
              )}
              <span>{toast.message}</span>
              <button className="toast-close" onClick={() => setToast(null)}>
                <FiX size={16} />
              </button>
            </div>
          )}

          {/* Scrollable Content Container */}
          <div
            className="integrations-scroll-container"
            style={{
              padding: "24px 32px",
              flex: 1,
              overflowY: "auto",
            }}
          >
            {/* Content Card */}
            <div
              style={{
                background: "var(--background)",
                borderRadius: "16px",
                border: "1px solid var(--border-color)",
                minHeight: "calc(100vh - 140px)",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
              }}
            >
            {renderIntegrationsContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationPopup
        show={showLogoutModal}
        handleClose={() => setShowLogoutModal(false)}
        handleConfirm={handleLogout}
        title="Do you really want to logout?"
        body="You will need to sign in again to access your account."
      />

      {/* Fireflies Webhook Setup Modal */}
      {renderFirefliesWebhookModal()}
    </>
  );
}

export default Integrations;
